import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check, Copy, AlertCircle, ArrowRight, ExternalLink, Wallet, Banknote } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  fetchAffiliateMe,
  fetchAffiliateConnectStatus,
  fetchWallet,
  setAffiliatePayoutMethod,
  startAffiliateOnboarding,
  cashOutWallet,
  type AffiliateMe,
  type Wallet as WalletData, setEventAlerts,
} from '@/services/affiliateApi';

/**
 * The member's referral link, what it has earned, and how it pays.
 *
 * Every paid Plus member has a link (minted automatically — nobody applies).
 * Rewards land as Hey Joe credit by default, spendable on any event ticket.
 * Anyone who would rather have cash connects Stripe and flips the switch here;
 * the same page is where Stripe's onboarding redirect returns to.
 */

const money = (cents?: number | null) => `$${((cents || 0) / 100).toFixed(2)}`;

const AffiliatePage: React.FC = () => {
  const { toast } = useToast();
  const [me, setMe] = useState<AffiliateMe | null>(null);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [cashingOut, setCashingOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, w] = await Promise.all([fetchAffiliateMe(), fetchWallet().catch(() => null)]);
      setMe(data);
      setWallet(w);
      // The /me flag is cached on the affiliates doc; connect/status re-reads Stripe,
      // which is what actually decides whether a cash payout can go out.
      if (data.isAffiliate) {
        try {
          const s = await fetchAffiliateConnectStatus();
          setMe(prev => (prev ? { ...prev, connectOnboarded: s.onboarded } : prev));
        } catch { /* /me's cached value stands */ }
      }
    } catch (err: any) {
      setError(err?.message || 'Could not load your referral details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Coming back from Stripe. Onboarding is asynchronous on Stripe's side, so this
  // re-reads rather than assuming success.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      toast({ title: 'Thanks!', description: "We're confirming your payout setup with Stripe." });
    } else if (params.get('refresh')) {
      toast({ title: 'Setup not finished', description: 'Pick up where you left off below.' });
    }
    if (params.get('connected') || params.get('refresh')) {
      window.history.replaceState({}, '', '/affiliate');
    }
  }, [toast]);

  const [copiedEvent, setCopiedEvent] = useState<string | null>(null);
  const copyEventLink = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedEvent(id);
      setTimeout(() => setCopiedEvent(c => (c === id ? null : c)), 2000);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the link and copy it manually.', variant: 'destructive' });
    }
  };
  const copyLink = async () => {
    if (!me?.link) return;
    try {
      await navigator.clipboard.writeText(me.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the link and copy it manually.', variant: 'destructive' });
    }
  };

  const onboard = async () => {
    setBusy(true);
    try {
      window.location.href = await startAffiliateOnboarding();
    } catch (err: any) {
      toast({ title: 'Could not start setup', description: err?.message, variant: 'destructive' });
      setBusy(false);
    }
  };

  const toggleAlerts = async (enabled: boolean) => {
    if (!me || (me.eventAlerts !== false) === enabled) return;
    setBusy(true);
    try {
      await setEventAlerts(enabled);
      setMe(prev => (prev ? { ...prev, eventAlerts: enabled } : prev));
    } catch (e: any) {
      toast({ title: 'Could not update event alerts', description: e?.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const choose = async (method: 'wallet' | 'cash') => {
    if (!me || me.payoutMethod === method) return;
    setBusy(true);
    try {
      await setAffiliatePayoutMethod(method);
      setMe(prev => (prev ? { ...prev, payoutMethod: method } : prev));
      toast({ title: method === 'wallet' ? 'Rewards will land as Hey Joe credit' : 'Rewards will be paid in cash' });
    } catch (err: any) {
      toast({ title: 'Could not update', description: err?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const card: React.CSSProperties = {
    padding: 20,
    borderRadius: 'var(--hj-r-md)',
    background: 'var(--hj-surface-1)',
    border: '1px solid var(--hj-hairline)',
  };

  const choiceStyle = (active: boolean): React.CSSProperties => ({
    flex: '1 1 200px',
    padding: '12px 14px',
    borderRadius: 'var(--hj-r-sm)',
    background: active ? 'var(--hj-yellow-tint)' : 'transparent',
    border: `1px solid ${active ? 'var(--hj-yellow)' : 'var(--hj-hairline)'}`,
    color: 'var(--hj-ink)',
    textAlign: 'left',
    cursor: busy ? 'default' : 'pointer',
    fontFamily: 'inherit',
  });

  const balance = wallet?.balanceCents ?? me?.walletCents ?? 0;
  // Thank-you credit (the October offer) expires; say how much is left and when.
  const bonusNote = (wallet?.bonuses || []).filter(b => b.remainingCents > 0).map((b, i) => (
    <p key={i} className="m-0" style={{ marginTop: 4, fontSize: 12.5, color: 'var(--hj-ink)' }}>
      Includes {money(b.remainingCents)} of thank-you credit{b.expiresAt ? `, good until ${new Date(b.expiresAt).toLocaleDateString()}` : ''}. It doesn’t apply to reader bookings.
    </p>
  ));
  const txTypeLabel = (t: string) => ({
    referral_reward: 'Referral reward',
    ticket_purchase: 'Ticket',
    refund: 'Refund',
    admin_grant: 'Credit from Hey Joe',
  } as Record<string, string>)[t] || t;

  return (
    <main className="mx-auto" style={{ maxWidth: 560, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ margin: '24px 0 20px', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
        <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-beige-ink-2)' }}>Referrals</div>
        <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(28px, 5vw, 38px)', lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 800, color: 'var(--hj-beige-ink)' }}>
          Your link. Your credit.
        </h1>
        <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
          Share Hey Joe with a friend. When they join Plus or register for an event through your link, you earn credit toward your next ticket.
        </p>
      </div>

      {loading ? (
        <div style={card}>
          <div className="flex items-center justify-center gap-2" style={{ padding: '16px 0', color: 'var(--hj-ink-2)' }}>
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        </div>
      ) : error ? (
        <div style={card}>
          <div className="flex items-start gap-2" style={{ fontSize: 13, color: 'hsl(var(--destructive))' }}>
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={load} className="font-medium" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-yellow-ink)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Try again
          </button>
        </div>
      ) : !me?.isAffiliate ? (
        <div className="flex flex-col gap-4">
          {balance > 0 && (
            <div style={card}>
              <div className="flex items-center gap-2 mono-label" style={{ color: 'var(--hj-ink-3)' }}><Wallet size={14} strokeWidth={1.8} /> Hey Joe credit</div>
              <div className="tabular" style={{ marginTop: 6, fontSize: 28, fontWeight: 800, fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-ink)' }}>{money(balance)}</div>
              <p className="m-0" style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink-2)' }}>Apply it at checkout on any event ticket.</p>
              {bonusNote}
            </div>
          )}
          <div style={card}>
            <p className="m-0" style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--hj-ink)' }}>
              Referral links come with Hey Joe Plus, and every reader with a published profile has one too. Join Plus, or set up as a reader, and yours is ready the moment you do.
            </p>
            <Link to="/plans" className="inline-flex items-center gap-1.5 font-semibold" style={{ marginTop: 14, padding: '10px 18px', borderRadius: 999, background: 'var(--hj-yellow)', color: '#111', fontSize: 13.5, textDecoration: 'none' }}>
              See Plus plans <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* The link */}
          <div style={card}>
            <p className="m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>Share this link</p>
            <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', marginTop: 3, lineHeight: 1.55 }}>
              Anyone who joins or registers after following it is credited to you, even if they sign
              up first and buy later. Sharing a specific class or event? Your short link for each one is below.
            </p>
            <div className="flex items-center gap-2" style={{ marginTop: 12, flexWrap: 'wrap' }}>
              <code style={{ flex: '1 1 260px', padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', fontSize: 13, color: 'var(--hj-ink)', overflowWrap: 'anywhere' }}>
                {me.link}
              </code>
              <button onClick={copyLink} className="flex items-center justify-center gap-2 font-semibold"
                style={{ padding: '10px 16px', borderRadius: 999, background: 'var(--hj-yellow)', border: 'none', color: '#111', fontSize: 13.5, cursor: 'pointer', minHeight: 40 }}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {me.active === false && (
              <p className="m-0" style={{ marginTop: 10, fontSize: 12.5, color: 'var(--hj-ink)' }}>
                This link is currently switched off. Anything you've already earned is unaffected.
              </p>
            )}
          </div>

          {/* One short link per upcoming event, code already on it */}
          {(me.eventLinks || []).length > 0 && (
            <div style={card}>
              <p className="m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>Your links for upcoming events</p>
              <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', marginTop: 3, lineHeight: 1.55 }}>
                Short enough for an Instagram story. Each one opens the event page with your code on it.
              </p>
              <div className="flex flex-col" style={{ marginTop: 10 }}>
                {me.eventLinks!.map((e) => (
                  <div key={e.productId} className="flex items-center gap-3" style={{ padding: '10px 0', borderTop: '1px solid var(--hj-hairline)', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <p className="m-0" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)', lineHeight: 1.35 }}>{e.title}</p>
                      <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink)', marginTop: 2 }}>
                        {new Date(e.eventDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        {' · '}<span style={{ overflowWrap: 'anywhere' }}>{e.link.replace(/^https:\/\//, '')}</span>
                      </p>
                    </div>
                    <button onClick={() => copyEventLink(e.productId, e.link)} className="flex items-center justify-center gap-1.5 font-semibold"
                      style={{ padding: '8px 14px', borderRadius: 999, background: copiedEvent === e.productId ? 'var(--hj-surface-2)' : 'var(--hj-yellow)', border: 'none', color: '#111', fontSize: 13, cursor: 'pointer', minHeight: 36 }}>
                      {copiedEvent === e.productId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copiedEvent === e.productId ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credit + earnings */}
          <div style={card}>
            <div className="flex flex-wrap items-end justify-between" style={{ gap: 16 }}>
              <div>
                <div className="flex items-center gap-2 mono-label" style={{ color: 'var(--hj-ink-3)' }}><Wallet size={14} strokeWidth={1.8} /> Hey Joe credit</div>
                <div className="tabular" style={{ marginTop: 6, fontSize: 30, fontWeight: 800, fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-ink)', lineHeight: 1.05 }}>{money(balance)}</div>
                <p className="m-0" style={{ marginTop: 6, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Apply it at checkout on any event ticket.</p>
                {bonusNote}
              </div>
              <div className="flex flex-wrap" style={{ gap: 20 }}>
                {[
                  { label: 'Referrals', value: String(me.sales ?? 0) },
                  { label: 'Earned', value: money(me.paidCents) },
                  { label: 'On the way', value: money((me.pendingCents || 0) + (me.heldCents || 0)) },
                ].map(s => (
                  <div key={s.label}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}>{s.value}</div>
                    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <p className="m-0" style={{ marginTop: 14, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.6 }}>
              Rewards arrive {me.holdDays ?? 31} days after your friend's purchase, once the refund window has closed.
            </p>
            {/* Credit was a one-way door until now: the payout switch only steered
                FUTURE rewards, so anything already banked as credit was stuck being
                credit. What you earned, you can move to your bank whenever. What we
                put there — a make-good, a prize — stays credit, which is why the
                cashable figure can be lower than the balance. */}
            {(wallet?.cashableCents ?? 0) > 0 && (
              <div className="flex flex-wrap items-center justify-between" style={{ gap: 12, marginTop: 14, padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)' }}>
                    {money(wallet!.cashableCents!)} of this is yours in cash
                  </div>
                  <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                    {wallet!.cashableCents! < balance
                      ? 'The rest is credit we added, so it stays on your account.'
                      : 'Move it to your bank, or leave it here and spend it on tickets.'}
                  </p>
                </div>
                {wallet?.payoutOnboarded ? (
                  <button
                    type="button"
                    disabled={cashingOut}
                    onClick={async () => {
                      setCashingOut(true);
                      try {
                        const r = await cashOutWallet();
                        setWallet(w => (w ? { ...w, balanceCents: r.balanceCents, cashableCents: r.cashableCents } : w));
                        toast({ title: `${money(r.cents)} on its way to your bank` });
                        load();
                      } catch (e: any) {
                        toast({ title: 'Could not cash out', description: e.message, variant: 'destructive' });
                      } finally { setCashingOut(false); }
                    }}
                    className="inline-flex items-center gap-2"
                    style={{ padding: '9px 16px', borderRadius: 999, background: 'var(--hj-ink)', color: 'var(--hj-bg)', border: 0, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: cashingOut ? 0.6 : 1 }}
                  >
                    {cashingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote size={15} strokeWidth={1.8} />}
                    Cash out {money(wallet!.cashableCents!)}
                  </button>
                ) : (
                  <button type="button" onClick={onboard} disabled={busy}
                    className="inline-flex items-center gap-2"
                    style={{ padding: '9px 16px', borderRadius: 999, background: 'var(--hj-ink)', color: 'var(--hj-bg)', border: 0, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    <Banknote size={15} strokeWidth={1.8} /> Set up payouts to cash out
                  </button>
                )}
              </div>
            )}
            {wallet && wallet.transactions.length > 0 && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--hj-hairline)' }}>
                {wallet.transactions.slice(0, 8).map(t => (
                  <div key={t.id} className="flex items-center justify-between" style={{ padding: '9px 0', borderBottom: '1px solid var(--hj-hairline)', fontSize: 13 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--hj-ink)', fontWeight: 600 }}>{txTypeLabel(t.type)}{t.productTitle ? ` · ${t.productTitle}` : ''}{t.sales ? ` · ${t.sales} referral${t.sales === 1 ? '' : 's'}` : ''}</div>
                      <div style={{ color: 'var(--hj-ink-3)', fontSize: 11.5 }}>{new Date(t.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                    <div className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontWeight: 700, color: t.amountCents < 0 ? 'var(--hj-ink-2)' : 'var(--hj-ink)' }}>
                      {t.amountCents < 0 ? '−' : '+'}{money(Math.abs(t.amountCents))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Event alerts: one weekly email of paid events, each with your link */}
          <div style={card}>
            <p className="m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>Event alerts</p>
            <p className="m-0" style={{ marginTop: 6, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.6 }}>
              Once a week, on Thursday, we email you the paid events coming up, each with your link, so you can share them while people are deciding.
            </p>
            <div className="flex flex-wrap" style={{ gap: 10, marginTop: 10 }}>
              <button type="button" onClick={() => toggleAlerts(true)} disabled={busy} style={choiceStyle(me.eventAlerts !== false)}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>On</div>
              </button>
              <button type="button" onClick={() => toggleAlerts(false)} disabled={busy} style={choiceStyle(me.eventAlerts === false)}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>Off</div>
              </button>
            </div>
          </div>

          {/* How rewards pay */}
          <div style={card}>
            <p className="m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>How you get rewarded</p>
            <div className="flex flex-wrap" style={{ gap: 10, marginTop: 10 }}>
              <button type="button" onClick={() => choose('wallet')} disabled={busy} style={choiceStyle(me.payoutMethod !== 'cash')}>
                <div className="flex items-center gap-2" style={{ fontWeight: 700, fontSize: 13.5 }}><Wallet size={15} strokeWidth={1.8} /> Hey Joe credit</div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>Lands on your balance, no setup. Spend it on any event ticket.</div>
              </button>
              <button type="button" onClick={() => choose('cash')} disabled={busy} style={choiceStyle(me.payoutMethod === 'cash')}>
                <div className="flex items-center gap-2" style={{ fontWeight: 700, fontSize: 13.5 }}><Banknote size={15} strokeWidth={1.8} /> Cash to my bank</div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>Paid through Stripe. Needs a one-time payout setup.</div>
              </button>
            </div>
            {me.payoutMethod === 'cash' && (
              me.connectOnboarded ? (
                <div className="flex items-center gap-2" style={{ marginTop: 12, fontSize: 13.5, color: 'var(--hj-green)', fontWeight: 600 }}>
                  <Check className="h-4 w-4" /> Your payouts are set up
                </div>
              ) : (
                <>
                  <p className="m-0" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.6 }}>
                    Connect Stripe so rewards can reach your bank. What you've earned keeps adding up either way, and goes out once you're set up.
                  </p>
                  <button onClick={onboard} disabled={busy}
                    className="flex items-center justify-center gap-2 font-semibold disabled:cursor-not-allowed"
                    style={{ marginTop: 12, padding: '12px 20px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow)', border: 'none', color: '#111', fontSize: 14.5, cursor: busy ? 'default' : 'pointer', width: '100%' }}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Set up my payouts
                    {!busy && <ArrowRight className="h-4 w-4" />}
                  </button>
                  <p className="m-0" style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: 'var(--hj-ink-2)' }}>
                    Payments are processed securely by Stripe. Hey Joe never sees your bank details.
                  </p>
                </>
              )
            )}
          </div>

          <p className="m-0" style={{ textAlign: 'center', fontSize: 12 }}>
            <a href="https://plus.heyjoe.io/calendar" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1" style={{ color: 'var(--hj-yellow-ink)' }}>
              See what's coming up to share <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      )}
    </main>
  );
};

export default AffiliatePage;
